import React, { useEffect, useState } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { DOC_CATEGORIES } from '@/lib/constants';
import { uploadFile, fileNameFromUrl } from '@/lib/storage';

export default function DocumentSlideOver({ open, onClose, document: doc, onSaved, contacts = [], developers = [], pms = [], projects = [] }) {
    const { profile } = useAuth();
    const isEdit = !!doc?.id;
    const [form, setForm] = useState({});
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(doc ? { ...doc } : { name: '', category: 'Other' });
        setFile(null);
    }, [open, doc?.id]);

    if (!open) return null;
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!isEdit && !file) { toast.error('Please select a file to upload'); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            for (const k of Object.keys(payload)) if (payload[k] === '') payload[k] = null;

            if (file) {
                const { publicUrl } = await uploadFile({ folder: 'documents', file, organizationId: profile.organization_id });
                payload.file_url = publicUrl;
                if (!payload.name) payload.name = file.name;
            }

            if (isEdit) {
                const { error } = await supabase.from('documents').update(payload).eq('id', doc.id);
                if (error) throw error;
            } else {
                payload.organization_id = profile.organization_id;
                payload.created_by = profile.id;
                const { error } = await supabase.from('documents').insert(payload);
                if (error) throw error;
            }
            toast.success(isEdit ? 'Document updated' : 'Document uploaded');
            onSaved?.();
            onClose();
        } catch (err) { toast.error(err.message); } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] flex justify-end" onClick={onClose}>
            <div className="absolute inset-0 bg-black/40" />
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" data-testid="document-slideover">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
                    <h2 className="text-xl font-bold">{isEdit ? 'Edit document' : 'Upload document'}</h2>
                    <button type="button" onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Name *"><input required value={form.name || ''} onChange={set('name')} className="input" data-testid="doc-name" placeholder="e.g. Trade License 2025" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Category *"><select required value={form.category || 'Other'} onChange={set('category')} className="input" data-testid="doc-category">{DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
                        <Field label="Expiry date"><input type="date" value={form.expiry_date || ''} onChange={set('expiry_date')} className="input" /></Field>
                    </div>

                    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mt-3">Link to (optional)</div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Contact"><select value={form.related_contact_id || ''} onChange={set('related_contact_id')} className="input"><option value="">—</option>{contacts.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}</select></Field>
                        <Field label="Developer"><select value={form.related_developer_id || ''} onChange={set('related_developer_id')} className="input"><option value="">—</option>{developers.map((d) => <option key={d.id} value={d.id}>{d.company_name}</option>)}</select></Field>
                        <Field label="Property manager"><select value={form.related_pm_id || ''} onChange={set('related_pm_id')} className="input"><option value="">—</option>{pms.map((p) => <option key={p.id} value={p.id}>{p.company_name}</option>)}</select></Field>
                        <Field label="Project"><select value={form.related_project_id || ''} onChange={set('related_project_id')} className="input"><option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}</select></Field>
                    </div>

                    <Field label={isEdit ? 'Replace file' : 'File *'}>
                        <div className="flex items-center gap-2">
                            <label className="flex-1 h-10 px-3 rounded-md border border-dashed border-gray-300 hover:border-aks-gold inline-flex items-center gap-2 cursor-pointer text-[13px] text-gray-600">
                                <Upload className="h-4 w-4" />
                                <span className="truncate">{file?.name || (form.file_url ? fileNameFromUrl(form.file_url) : 'Choose file')}</span>
                                <input type="file" className="hidden" accept="application/pdf,.doc,.docx,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                            </label>
                            {form.file_url && <a href={form.file_url} target="_blank" rel="noreferrer" className="h-10 px-3 inline-flex items-center gap-1.5 rounded-md border border-gray-300 text-[12px] font-semibold text-aks-navy"><FileText className="h-4 w-4" /> View</a>}
                        </div>
                    </Field>

                    <Field label="Notes"><textarea rows={3} value={form.notes || ''} onChange={set('notes')} className="input" /></Field>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3">
                    <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md border border-gray-300 font-medium">Cancel</button>
                    <button type="submit" disabled={saving} className="flex-1 h-11 rounded-md bg-aks-navy text-white font-semibold disabled:opacity-60" data-testid="doc-submit">{saving ? 'Saving…' : 'Save document'}</button>
                </div>
                <style>{`.input{margin-top:.25rem;width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #d1d5db;border-radius:.375rem;outline:none;font-size:14px;background:#fff}.input:focus{border-color:#C9A84C;box-shadow:0 0 0 3px rgba(201,168,76,.25)}textarea.input{height:auto;padding:.5rem .75rem}`}</style>
            </form>
        </div>
    );
}

function Field({ label, children }) { return <label className="block"><span className="text-[12px] font-medium text-aks-text">{label}</span>{children}</label>; }
